// PropertyTabExportMenu
//
// Context-aware export dropdown for the property detail page. Renders a
// different menu depending on which tab the user is currently viewing.
// Sync exports run immediately; async bundles enqueue an export job and
// redirect the user to the "Exports" tab where they can track status.
//
// Tabs covered: assets, site-attributes, photos, 3d (files & documents).
// Other tabs render nothing.

import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/shared/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import { Download, FileSpreadsheet, FileJson, Image as ImageIcon, Package, Loader2, AlertTriangle } from "lucide-react";
import { toast } from "@/shared/hooks/use-toast";
import {
  exportDeficiencies,
  exportAssets,
  exportSurvey,
  type AssetExportFormat,
} from "@/features/exports/services";
import { buildPropertyPhotosBundle, buildSiteBundle } from "@/features/exports/services/bundles";
import { useDownloadManager } from "@/features/exports/services/DownloadManagerContext";
import { sanitize } from "@/features/exports/services/_helpers";

type SupportedTab = "assets" | "site-attributes" | "photos" | "3d" | "deficiencies";

interface Props {
  activeTab: string;
  clientId: string;
  propertyId: string;
  propertyLabel: string;
  // Called after async bundle is enqueued so the page can switch to the Exports tab.
  onJumpToExportsTab?: () => void;
}

export function PropertyTabExportMenu({
  activeTab,
  clientId,
  propertyId,
  propertyLabel,
  onJumpToExportsTab,
}: Props) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const { enqueue } = useDownloadManager();

  // Only show on tabs we support.
  if (!isSupportedTab(activeTab)) return null;

  const goToExports = () => {
    if (onJumpToExportsTab) {
      onJumpToExportsTab();
    } else {
      navigate(`/property/${propertyId}/exports`);
    }
  };

  const slug = sanitize(propertyLabel);

  const handleAssetExport = async (format: AssetExportFormat) => {
    setBusy(true);
    try {
      await exportAssets({
        projectId: clientId,
        propertyId,
        label: propertyLabel,
        format,
      });
      toast({ title: `Asset register exported as ${format.toUpperCase()}` });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleSurveyExport = async (format: "xlsx" | "csv") => {
    setBusy(true);
    try {
      await exportSurvey({
        projectId: clientId,
        propertyId,
        projectName: propertyLabel,
        format,
      });
      toast({ title: `Survey exported as ${format.toUpperCase()}` });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDeficiencyExport = async () => {
    setBusy(true);
    try {
      await exportDeficiencies({
        projectId: clientId,
        propertyId,
        label: propertyLabel,
        format: "csv",
      });
      toast({ title: "Deficiencies exported as CSV" });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err instanceof Error ? err.message : String(err),
        variant: "destructive",
      });
    } finally {
      setBusy(false);
    }
  };

  const handlePhotoBundle = () => {
    enqueue({
      label: `Photo bundle — ${propertyLabel}`,
      filename: `${slug}_photos.zip`,
      build: (ctx) => buildPropertyPhotosBundle({ projectId: clientId, propertyId }, ctx),
    });
  };

  const handleSiteBundle = () => {
    enqueue({
      label: `Site bundle — ${propertyLabel}`,
      filename: `${slug}_site_bundle.zip`,
      build: (ctx) => buildSiteBundle({ projectId: clientId, propertyId }, ctx),
    });
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" disabled={busy} className="gap-2">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Export all
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {activeTab === "assets" && (
          <>
            <DropdownMenuItem onClick={() => handleAssetExport("xlsx")}>
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Asset register (XLSX)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAssetExport("csv")}>
              <Download className="h-4 w-4 mr-2" /> Asset register (CSV)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleAssetExport("json")}>
              <FileJson className="h-4 w-4 mr-2" /> Asset register (JSON)
            </DropdownMenuItem>
          </>
        )}

        {activeTab === "site-attributes" && (
          <>
            <DropdownMenuItem onClick={() => handleSurveyExport("xlsx")}>
              <FileSpreadsheet className="h-4 w-4 mr-2" /> Survey data (XLSX)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleSurveyExport("csv")}>
              <Download className="h-4 w-4 mr-2" /> Survey data (CSV)
            </DropdownMenuItem>
          </>
        )}

        {activeTab === "photos" && (
          <DropdownMenuItem onClick={handlePhotoBundle}>
            <ImageIcon className="h-4 w-4 mr-2" /> Bundle all photos (zip)
          </DropdownMenuItem>
        )}

        {activeTab === "3d" && (
          <DropdownMenuItem onClick={handleSiteBundle}>
            <Package className="h-4 w-4 mr-2" /> Download everything (zip)
          </DropdownMenuItem>
        )}

        {activeTab === "deficiencies" && (
          <DropdownMenuItem onClick={handleDeficiencyExport}>
            <AlertTriangle className="h-4 w-4 mr-2" /> Deficiencies (CSV)
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={goToExports}>
          <Package className="h-4 w-4 mr-2" /> More exports →
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function isSupportedTab(tab: string): tab is SupportedTab {
  return tab === "assets" || tab === "site-attributes" || tab === "photos" || tab === "3d" || tab === "deficiencies";
}
